import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { useLang, tr } from "../contexts/LangContext";
import api from "../lib/api";
import { ArrowRight, Calendar, MapPin, FileText, Award, ListOrdered, X, Download } from "lucide-react";

const DOC_TABS = [
  { key: "notice_of_race",       icon: FileText,    color: "text-club-red"  },
  { key: "sailing_instructions", icon: FileText,    color: "text-navy"      },
  { key: "results",              icon: ListOrdered, color: "text-club-yellow-dark" },
  { key: "general_ranking",      icon: Award,       color: "text-green-700" },
];

export default function Regattas() {
  const { t, lang, img } = useLang();
  const [regattas, setRegattas] = useState([]);
  const [tab, setTab] = useState("upcoming"); // "upcoming" | "archive"
  const [popup, setPopup] = useState(null); // {regatta}

  useEffect(() => {
    api.get("/regattas").then((r) => setRegattas(r.data)).catch(() => {});
  }, []);

  // Split by date — today included in "upcoming"
  const todayIso = new Date().toISOString().slice(0, 10);
  const upcoming = regattas.filter((r) => (r.date || "") >= todayIso);
  const archive  = regattas
    .filter((r) => (r.date || "") < todayIso)
    .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  const list = tab === "upcoming" ? upcoming : archive;
  const isArchive = tab === "archive";

  return (
    <div data-testid="regattas-page">
      <PageHeader eyebrow={t.regattas.eyebrow} title={t.regattas.title} lead={t.regattas.lead}
        image={img("regattas", "header", "https://images.unsplash.com/photo-1498623116890-37e912163d5d?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400")} />

      <section className="pb-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          {/* Upcoming / Archive tabs */}
          <div className="flex gap-3 border-b border-slate-200 mb-12" data-testid="regattas-tabs">
            <button
              type="button"
              onClick={() => setTab("upcoming")}
              className={`px-6 py-4 text-sm uppercase tracking-[0.2em] border-b-2 transition-colors ${tab === "upcoming" ? "border-club-red text-club-red font-semibold" : "border-transparent text-slate-500 hover:text-navy"}`}
              data-testid="regattas-tab-upcoming"
            >
              {t.regattas.tabUpcoming || tr("À venir")} <span className="ml-1 text-[10px] opacity-70">({upcoming.length})</span>
            </button>
            <button
              type="button"
              onClick={() => setTab("archive")}
              className={`px-6 py-4 text-sm uppercase tracking-[0.2em] border-b-2 transition-colors ${tab === "archive" ? "border-club-red text-club-red font-semibold" : "border-transparent text-slate-500 hover:text-navy"}`}
              data-testid="regattas-tab-archive"
            >
              {t.regattas.tabArchive || "Archive"} <span className="ml-1 text-[10px] opacity-70">({archive.length})</span>
            </button>
          </div>

          {list.length === 0 && (
            <div className="bg-sand border border-dashed border-slate-300 p-12 text-center text-slate-500" data-testid="regattas-empty">
              {isArchive
                ? (t.regattas.archiveEmpty || tr("Aucune régate passée pour le moment."))
                : (t.regattas.upcomingEmpty || tr("Aucune régate prévue prochainement."))}
            </div>
          )}

          <div className="space-y-12">
          {list.map((r, i) => (
            <div
              key={r.id}
              className={`group grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""}`}
              data-testid={`regatta-card-${r.slug}`}
            >
              <div className="lg:col-span-7">
                <Link to={`/regattas/${r.slug}`} className="block">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={r.image} alt={r.name} className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ${isArchive ? "grayscale-[35%]" : ""}`} />
                  </div>
                </Link>
              </div>
              <div className="lg:col-span-5">
                <div className="text-[10px] uppercase tracking-[0.3em] text-club-yellow-dark font-semibold mb-3">
                  {isArchive ? (t.regattas.archiveLabel || "Archive") : tr("Régate")} · {i + 1}/{list.length}
                </div>
                <h2 className="font-serif text-4xl lg:text-5xl font-light text-navy">{r.name}</h2>
                <div className="divider-gold mt-5" />
                <p className="text-slate-600 mt-6 leading-relaxed">{r[`description_${lang}`]}</p>
                <div className="flex flex-wrap gap-4 mt-6 text-xs text-slate-500">
                  <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-club-yellow" />{new Date(r.date).toLocaleDateString(lang === "en" ? "en-GB" : lang)}</span>
                  <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-club-yellow" />{r.location}</span>
                </div>
                <div className="flex flex-wrap gap-3 mt-6">
                  <button
                    onClick={() => setPopup({ regatta: r, defaultTab: isArchive ? "results" : "notice_of_race" })}
                    className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] px-4 py-2 transition-colors ${isArchive ? "bg-club-yellow text-navy border border-club-yellow hover:bg-club-yellow-dark" : "text-navy border border-navy hover:bg-navy hover:text-white"}`}
                    data-testid={`regatta-docs-${r.slug}`}
                  >
                    {isArchive ? <Award className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
                    {isArchive ? (t.regattas.viewResults || tr("Voir les classements")) : "Documents"}
                  </button>
                  <Link to={`/regattas/${r.slug}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-club-red border-b border-club-red pb-1 hover:text-club-red-dark">
                    {t.common.readMore} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </Link>
                </div>
              </div>
            </div>
          ))}
          </div>
        </div>
      </section>

      {popup && <RegattaDocsPopup regatta={popup.regatta} defaultTab={popup.defaultTab} onClose={() => setPopup(null)} t={t} lang={lang} />}
    </div>
  );
}

function RegattaDocsPopup({ regatta, onClose, t, lang, defaultTab = "notice_of_race" }) {
  const [docs, setDocs] = useState([]);
  const [tab, setTab] = useState(defaultTab);
  const backend = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    api.get(`/regattas/${regatta.slug}/documents`).then((r) => setDocs(r.data)).catch(() => {});
  }, [regatta.slug]);

  const DOC_LABEL = {
    notice_of_race:       t.regattas.noticeOfRace,
    sailing_instructions: t.regattas.sailingInstructions,
    results:              t.regattas.results,
    general_ranking:      t.regattas.generalRanking || tr("Classement général"),
  };

  const current = docs.filter((d) => d.doc_type === tab);

  return (
    <div className="fixed inset-0 z-[60] bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="regatta-popup" onClick={onClose}>
      <div className="bg-white max-w-3xl w-full p-8 relative my-12" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-navy" data-testid="regatta-popup-close">
          <X className="w-5 h-5" />
        </button>
        <div className="label-eyebrow text-club-red mb-2">Documents officiels</div>
        <h2 className="font-serif text-3xl text-navy">{regatta.name}</h2>
        <div className="text-xs text-slate-500 mt-1">
          {new Date(regatta.date).toLocaleDateString(lang === "en" ? "en-GB" : lang)} · {regatta.location}
        </div>
        <div className="divider-gold mt-4 mb-6" />

        <div className="flex flex-wrap gap-1 border-b border-slate-200 mb-6" data-testid="regatta-popup-tabs">
          {DOC_TABS.map(({ key, icon: Icon, color }) => {
            const count = docs.filter((d) => d.doc_type === key).length;
            return (
              <button
                key={key} onClick={() => setTab(key)}
                className={`px-3 py-2.5 text-[11px] uppercase tracking-[0.2em] border-b-2 flex items-center gap-2 ${
                  tab === key ? "border-club-red text-club-red font-semibold" : "border-transparent text-slate-500 hover:text-navy"
                }`}
                data-testid={`popup-tab-${key}`}
              >
                <Icon className={`w-3.5 h-3.5 ${tab === key ? "" : color}`} />
                {DOC_LABEL[key]} {count > 0 && <span className="ml-1 text-[9px] bg-club-yellow text-navy px-1.5 py-0.5">{count}</span>}
              </button>
            );
          })}
        </div>

        {current.length === 0 ? (
          <div className="bg-sand border border-dashed border-slate-300 p-8 text-center text-slate-500" data-testid="popup-empty">
            Aucun document disponible pour cette catégorie.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {current.map((d) => (
              <li key={d.id} className="py-3 flex items-center justify-between gap-3" data-testid={`popup-doc-${d.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-navy">
                    {d.race_number > 0 && <span className="text-xs uppercase tracking-widest text-club-yellow-dark mr-2">Course #{d.race_number}</span>}
                    {d.title}
                  </div>
                  <div className="text-xs text-slate-500">{new Date(d.created_at).toLocaleDateString(lang === "en" ? "en-GB" : lang)}</div>
                </div>
                <a
                  href={`${backend}${d.url}`} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-club-red border border-club-red hover:bg-club-red hover:text-white px-3 py-2"
                  data-testid={`popup-dl-${d.id}`}
                >
                  <Download className="w-3.5 h-3.5" /> PDF
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
